"use client";

import type { Media } from "@mw/shared/payload-types";

import { Image } from "@ui/components/core/Image";
import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { type FC, type ReactNode } from "react";

export interface AccordionItem {
  badge?: null | string;
  description?: ReactNode;
  icon?: Media;
  title: string;
}

export interface AccordionItemsProps {
  items: AccordionItem[];
  onSelect: (idx: number) => void;
  selectedIdx: number;
}

const AccordionItem = ({
  item,
  idx,
  selectedIdx,
  onSelect,
}: {
  idx: number;
  item: AccordionItem;
  onSelect: (idx: number) => void;
  selectedIdx: number;
}) => {
  return (
    <li className='relative' key={idx}>
      <button aria-expanded={idx === selectedIdx} className='group text-start' onClick={() => onSelect(idx)}>
        <h3
          className={cn(
            "f-heading-05 flex flex-nowrap items-start gap-8 transition-transform duration-150 ease-out",
            idx !== selectedIdx && "opacity-40 transition-opacity duration-150 ease-out hover:opacity-60",
          )}
        >
          {item.icon && <Image className='mt-4 h-20 w-20' resource={item.icon} />}
          {item.title}
          {item.badge && (
            <span className='rounded-full border border-default px-8 py-4 text-xs text-light'>{item.badge}</span>
          )}
        </h3>
        {item.description && (
          <m.div
            animate={{
              height: idx === selectedIdx ? "auto" : 0,
              opacity: idx === selectedIdx ? 1 : 0,
            }}
            aria-hidden={idx === selectedIdx ? "false" : "true"}
            className={cn("f-paragraph-02 overflow-hidden text-light")}
            initial={false}
            transition={{ duration: 0.3, type: "spring", stiffness: 320, damping: 32 }}
          >
            <m.div
              animate={{
                opacity: idx === selectedIdx ? 1 : 0,
              }}
              className='origin-left whitespace-pre-line pb-4 pt-8'
              initial={false}
              transition={{
                duration: 0.3,
                delay: idx === selectedIdx ? 0.1 : 0,
                type: "spring",
                stiffness: 320,
                damping: 32,
              }}
            >
              {item.description}
            </m.div>
          </m.div>
        )}
      </button>
    </li>
  );
};

const AccordionItems: FC<AccordionItemsProps> = ({ items, selectedIdx, onSelect }) => {
  return (
    <ul className='flex flex-col gap-24'>
      {items.map((item, idx) => (
        <AccordionItem idx={idx} item={item} key={idx} onSelect={onSelect} selectedIdx={selectedIdx} />
      ))}
    </ul>
  );
};

export default AccordionItems;
